import React, { memo, useCallback } from 'react';
import { FlatList, Keyboard, StyleSheet, TouchableWithoutFeedback } from 'react-native';

import AlertStraightTriangleIcon from '@/assets/icons/alertTriangleWhite';
import { InnerBorder } from '@/components/InnerBorder';
import { LineSeparator } from '@/components/LineSeparator';
import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useShowBonusToggle } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { designSystem } from '@/styles/styles';

import { useSbkBetSlipStore } from '../../hooks/use-sbk-betslip-store';
import { BetIds } from '../../utils/betslip-utils';
import { Banner } from '../Banner';
import { BonusToggleRow } from '../BonusToggle';
import { SbkPromoWidget } from '../SbkPromoWidget';
import { BetInputs } from '../bet-inputs/BetInputs';
import { ParlayEventGroup } from './ParlayEventGroup';
import { ParlayOdds } from './ParlayOdds';
import { ParlayTitle } from './ParlayTitle';
import { useParlayCardAlert } from './hooks/use-parlay-card-alert';

const keyExtractor = (eventId: string) => `event${eventId}`;

const ItemSeparator = () => <LineSeparator style={styles.separator} />;

const ParlayHeaderSkeleton = () => (
    <Row alignItems="center" gap="s40">
        <Box flex={1}>
            <LoadingSkeleton
                skeletonColor={designSystem.colors.neutral300}
                backgroundColor={ds.colorToken.background.neutral.subtle}
                borderRadius="r6"
                height={styles.titleSkeleton.height}
            />
        </Box>
        <LoadingSkeleton
            skeletonColor={designSystem.colors.neutral300}
            backgroundColor={ds.colorToken.background.neutral.subtle}
            borderRadius="r6"
            width={styles.oddsSkeleton.width}
            height={styles.oddsSkeleton.height}
        />
    </Row>
);

export const ParlayCard = memo(() => {
    const orderedEventIds = useSbkBetSlipStore(state => state.eventOrder);
    const showComboOddsLoading = useSbkBetSlipStore(state => state.showComboOddsLoading);
    const betId = BetIds.COMBO;
    const alert = useParlayCardAlert();
    const showBonusToggle = useShowBonusToggle();

    const renderItem = useCallback(({ item: eventId }: { item: string }) => {
        return <ParlayEventGroup eventId={eventId} />;
    }, []);

    const renderHeader = () => {
        if (showComboOddsLoading) {
            return <ParlayHeaderSkeleton />;
        }

        if (alert) {
            return (
                <Box gap="s16" mb="s16">
                    <Banner
                        leftIcon={<AlertStraightTriangleIcon />}
                        message={alert.message}
                        backgroundColor={ds.colorToken.background.neutral.subtle}
                        textColor={ds.colorToken.text.primary}
                    />
                </Box>
            );
        }

        return (
            <Row justifyContent="space-between" alignItems="center">
                <ParlayTitle />
                <ParlayOdds variant="titleMedium" color={ds.colorToken.text.brand} />
            </Row>
        );
    };

    return (
        <SbkPromoWidget betId={BetIds.COMBO}>
            <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
                <Box borderRadius="r20" backgroundColor={ds.colorToken.background.neutral.subtler} p="s16">
                    <InnerBorder borderRadius="r20" borderColor={ds.colorValue.border.subtle} />
                    {renderHeader()}
                    {!alert || showComboOddsLoading ? (
                        <Box mb="s16">
                            <BetInputs betId={betId} />
                            {showBonusToggle ? (
                                <>
                                    <BonusToggleRow betId={betId} />
                                    <LineSeparator style={styles.bonusToggleSeparator} />
                                </>
                            ) : null}
                        </Box>
                    ) : null}
                    <FlatList
                        keyExtractor={keyExtractor}
                        showsVerticalScrollIndicator={false}
                        scrollEnabled={false}
                        data={orderedEventIds}
                        renderItem={renderItem}
                        contentContainerStyle={styles.flatList}
                        ItemSeparatorComponent={ItemSeparator}
                    />
                </Box>
            </TouchableWithoutFeedback>
        </SbkPromoWidget>
    );
});

const styles = StyleSheet.create({
    flatList: {
        paddingVertical: 4,
    },
    separator: {
        marginVertical: 12,
    },
    titleSkeleton: {
        height: 24,
    },
    oddsSkeleton: {
        width: 60,
        height: 24,
    },
    bonusToggleSeparator: {
        marginTop: 16,
    },
});
